import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  useStockProduct,
  useWarehouseStockEnabled,
} from 'src/sections/@dashboard/warehouse/hooks/useClinicalStockProducts';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import {
  STOCK_FIELD_NAMES,
  openedTakingProductId,
  repeatsSavedTaking,
} from './medicationStockValues';
import { useStockIssueDefault } from './useStockIssueDefault';

interface MedicationStockIssueDefaultProps {
  /** The host holds the form: an unconfirmed save or a save in flight. */
  disabled?: boolean;
  /** The host's form session: a new one forgets what was set before. */
  session?: number;
}

/**
 * #692 rev B (P7-R4): the "Magazyn" step's computed issue, kept by the step
 * body rather than the step — the dose is typed two steps before Magazyn
 * and may change after the vet saw it. It also tells the schema and the
 * payload whether the Product is Rx, so a save from the summary (every step
 * is reachable while editing) still asks for the prescription. Renders
 * nothing.
 */
export default function MedicationStockIssueDefault({
  disabled,
  session = 0,
}: MedicationStockIssueDefaultProps): null {
  const { watch, getValues, setValue, formState } =
    useFormContext<MedicationsFormModalValuesProps>();
  const [enabled, productId] = watch(['stockEnabled', 'stockProductId']);
  const flaggedRx = watch('stockProductIsRx');
  // Every field of the taking: a change to any is no longer a repeat (P7B-R5).
  watch(STOCK_FIELD_NAMES);
  const warehouseEnabled = useWarehouseStockEnabled();
  // The step's own query: one cache entry, no second request.
  const product = useStockProduct(enabled ? productId : '');
  const saved = formState.defaultValues;
  // P7B-R4/R5: a saved taking left as it was is a repeat, and the backend
  // compares its stored prescription answer — so the saved word on Rx stands
  // both ways, whatever the Product says now. Changed, it is a new supply,
  // Rx if either says so. Any other Product is Rx only once read (P7B-R1).
  const savedRx = Boolean(saved?.stockProductIsRx);
  const isRx = repeatsSavedTaking(getValues(), saved)
    ? savedRx
    : (productId === openedTakingProductId(saved) && savedRx) ||
      Boolean(product?.isRx);
  // Compared with the form, not only on a change of Product: this host
  // outlives a reset for the next line, which reseeds the flag.
  useEffect(() => {
    if (flaggedRx !== isRx) setValue('stockProductIsRx', isRx);
  }, [flaggedRx, isRx, setValue]);
  useStockIssueDefault(
    enabled && Boolean(productId) && !disabled && warehouseEnabled,
    product,
    session,
    // P7B-R13: a Product not read yet (or never) may be Rx, so the handover
    // is not guessed for it; the prescription rules keep the flag.
    product ? isRx : true,
  );
  return null;
}
